import classNames from "classnames";
import React from "react";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { LoadingSpinner } from "../../../common/LoadingSpinner";
import { RichText } from "../../../common/RichText";
import { ProductCompareTheme } from "../../../constants";
import { ISafeHTML, IWebPageURL, isoSafeHTML } from "../../../models/nominals";
import {
    IAttributeOptionGroup,
    ICompareTile,
    IGroupColumnsHeader,
    ITileID,
} from "../../../models/product-compare";
import { SizeSelector } from "../containers/ProductCompareSizeSelector";
import { ProductSelectionGridColumn } from "../elements/ProductSelectionGridColumn";
import { getHighestVisibleAttributeCount } from "../utility";

import styles from "./ProductSelectionGrid.module.scss";

interface IProps {
    header: ISafeHTML;
    productComparePageURL: IWebPageURL | null;
    tiles: readonly ICompareTile[];
    showStartingAt?: boolean;
    showRating?: boolean;
    showInStoreCallout?: boolean;
    ctaText?: string;
    selectedTileIDs: readonly ITileID[];
    preselectedSize: string | null;
    priceSelectValue: string;
    financingLink: IWebPageURL;
    sizeAttributeOptionGroup: IAttributeOptionGroup | null;
    theme: ProductCompareTheme;
    footer: ISafeHTML | null;
    groupHeader?: IGroupColumnsHeader | null;
    showSizeSelector?: boolean | null;
    addToSelected: (tileID: ITileID) => void;
    removeFromSelected: (tileID: ITileID) => void;
    showModal: () => void;
}

export const ProductSelectionGrid = (props: IProps): React.ReactNode => {
    // TODO(i18n): Use t tagged template for default value: t`Compare All Mattresses`
    const ctaText = props.ctaText ? props.ctaText : "Compare All Mattresses";

    const buildTileIDString = (): string => {
        const tileIDs = props.tiles.map((tile) => {
            return tile.product_compare_tile.id;
        });

        return tileIDs.join(",");
    };

    const buildHeaderContent = (url: string): ISafeHTML => {
        if (props.productComparePageURL) {
            const link = `<a href="${url}">${ctaText}</a>`;
            return isoSafeHTML.wrap(`${props.header}${link}`);
        }
        return isoSafeHTML.wrap(`${props.header}`);
    };

    const window = getWindow();
    const isThemeOne = props.theme === ProductCompareTheme.THEME_ONE;
    const url = `${
        props.productComparePageURL
    }?tiles=${buildTileIDString()}&return_url=${encodeURI(
        window?.location.pathname ?? "/",
    )}`;
    const activeTiles = props.tiles.filter(
        (tile) => tile.is_promo_period_active !== false,
    );
    const allTilesLength = activeTiles.length;
    const groupedColumns =
        props.groupHeader && isThemeOne
            ? activeTiles.slice(Number(props.groupHeader.numColumns) * -1)
            : null;
    const firstGroupTiles =
        props.groupHeader && isThemeOne
            ? activeTiles.slice(
                  0,
                  allTilesLength - Number(props.groupHeader.numColumns),
              )
            : activeTiles;

    const headerClassNames = classNames({
        [styles.header]: true,
        [props.theme || ""]: isThemeOne,
    });
    const tilesClassNames = classNames({
        [styles.tiles]: true,
        [styles.tilesThemeOne]: isThemeOne,
    });
    const rootClasses = classNames({
        [styles.root]: true,
        [styles.rootThemeOne]: isThemeOne,
    });
    const highestVisibleAttributeCount =
        getHighestVisibleAttributeCount(firstGroupTiles);

    return (
        <div className={rootClasses}>
            <header className={headerClassNames}>
                <RichText html={buildHeaderContent(url)} />
            </header>
            {props.sizeAttributeOptionGroup && (
                <SizeSelector
                    sizeAttributeOptionGroup={props.sizeAttributeOptionGroup}
                    preselectedSize={props.preselectedSize}
                    showSizeSelector={props.showSizeSelector}
                    theme={props.theme}
                />
            )}
            <div className={tilesClassNames}>
                {activeTiles.length === 0 && <LoadingSpinner />}
                <div className={styles.groupTilesNormal}>
                    {props.groupHeader?.text.content && (
                        <div aria-hidden="true" className="sr-only">
                            <RichText
                                className={`${styles.specialColumnsHeader} ${
                                    props.groupHeader.text.alignment || ""
                                }`}
                                html={props.groupHeader.text.content}
                            />
                        </div>
                    )}
                    <div className="tiles">
                        {firstGroupTiles.map((tile) => (
                            <ProductSelectionGridColumn
                                key={`tile-${tile.product_compare_tile.id}`}
                                tile={tile}
                                highestAttributeCount={
                                    highestVisibleAttributeCount
                                }
                                {...props}
                            />
                        ))}
                    </div>
                </div>
                {isThemeOne && (
                    <div className={styles.groupTilesSpecial}>
                        {props.groupHeader?.text.content && (
                            <RichText
                                className={`${styles.specialColumnsHeader} ${
                                    props.groupHeader.text.alignment || ""
                                }`}
                                html={props.groupHeader.text.content}
                            />
                        )}
                        <div className="tiles">
                            {groupedColumns?.map((tile) => (
                                <ProductSelectionGridColumn
                                    key={`tile-${tile.product_compare_tile.id}`}
                                    tile={tile}
                                    {...props}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {props.productComparePageURL && (
                <a
                    className="button button--secondary button--compare-all al-compareallmattresses-whitebutton"
                    href={url}
                >
                    {ctaText}
                </a>
            )}
            {props.footer && (
                <footer>
                    <RichText html={props.footer} />
                </footer>
            )}
        </div>
    );
};
